import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import 'flag-icons/css/flag-icons.min.css';
import RefGuard from './components/RefGuard';
import ScrollToTop from './components/ScrollToTop';
import PostHogPageView from './components/PostHogPageView';
import Landing from './pages/Landing';
import Signup from './pages/Signup';
import Registration from './pages/Registration';
import SsoSignupDetails from './pages/SsoSignupDetails';
import SsoSignupContact from './pages/SsoSignupContact';
import Success from './pages/Success';
import ApplicantPortalRoot from './applicant/ApplicantPortalRoot';
import { ApplicantPortalContent } from './applicant/page';
import AuthCallback from './pages/AuthCallback';
import AdminCaseDetail from './pages/AdminCaseDetail';
import AgentConsole from './pages/AgentConsole';
import Dashboard from './pages/Dashboard';
import Chat from './pages/Chat';
import Account from './pages/Account';
import ReferPage from './pages/ReferPage';
import ReferralListPage from './pages/ReferralListPage';
import GettingStarted from './pages/GettingStarted';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Dpa from './pages/Dpa';
import CookiePolicy from './pages/CookiePolicy';
import ReferralRedirect from './pages/ReferralRedirect';
import PartnerLinkRedirect from './pages/PartnerLinkRedirect';
import SettingsPage from './pages/SettingsPage';
import ExistingUserWelcome from './pages/ExistingUserWelcome';
import ProfileSetup from './components/ProfileSetup';
import NotFound404 from './pages/NotFound404';
import LoginError from './pages/LoginError';
import Login from './pages/Login';
import OrgSignup from './pages/OrgSignup';
import OrgCreateProject from './pages/OrgCreateProject';
import OrgMovieMagicCrew from './pages/OrgMovieMagicCrew';
import OrgAdvisorDashboard from './pages/OrgAdvisorDashboard';
import OrgAdminTools from './pages/OrgAdminTools';
import OrgProductionDetails from './pages/OrgProductionDetails';
import OrgParticipantDetail from './pages/OrgParticipantDetail';
import OrgRequestDetail from './pages/OrgRequestDetail';
import CustomerSignup from './pages/CustomerSignup';
import FieldworkParticipant from './fieldwork';
import OrgResetPassword from './pages/OrgResetPassword';
import Qualify from './pages/Qualify';
import QualifyQualified from './pages/QualifyQualified';
import ProcedureShell from './pages/procedure/ProcedureShell';
import ProcedureLanding from './pages/procedure/ProcedureLanding';
import ProcedureIntakePage from './pages/procedure/ProcedureIntakePage';
import './pages/Admin.css';
import AdvisorPortalLayout from './layouts/AdvisorPortalLayout';
import AdvisorCustomerSearch from './pages/customer-search/page';
import AdvisorCustomerNew from './pages/customer-new/page';
import AdvisorCustomerProfile from './pages/customer-profile/page';
import AdvisorCaseDetail from './pages/case-detail/page';
import AdvisorFamilyView from './pages/family-view/page';
import {
  ADVISOR_HOME_PATH,
  getAuthRole,
  ORG_SLATE_PATH,
  PARTICIPANT_HOME_PATH,
} from './utils/postLoginNavigation';

const DEFAULT_LOCALE = 'es';
const localizedPath = (path) => `/${DEFAULT_LOCALE}${path}`;

function safeParseJwtExp(token) {
  try {
    if (typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json);
    const exp = payload?.exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

function isMissingOrExpiredToken(token) {
  if (!token) return true;
  const exp = safeParseJwtExp(token);
  if (!exp) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp <= nowSec;
}

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('authToken');
  if (isMissingOrExpiredToken(token)) return <Navigate to="/login" replace />;
  return children;
}

function LoginRedirectRoute({ children }) {
  const token = localStorage.getItem('authToken');
  if (isMissingOrExpiredToken(token)) return <Navigate to="/login" replace />;
  return children;
}

function AdvisorOnlyRoute({ children }) {
  const token = localStorage.getItem('authToken');
  if (isMissingOrExpiredToken(token)) return <Navigate to="/login" replace />;
  const role = getAuthRole();
  if (role !== 'advisor') {
    if (role === 'admin' || role === 'super_admin') {
      return <Navigate to={ORG_SLATE_PATH} replace />;
    }
    return <Navigate to={PARTICIPANT_HOME_PATH} replace />;
  }
  return children;
}

function BlockAdvisorFromOrg({ children }) {
  const token = localStorage.getItem('authToken');
  if (isMissingOrExpiredToken(token)) return <Navigate to="/login" replace />;
  if (getAuthRole() === 'advisor') {
    return <Navigate to={ADVISOR_HOME_PATH} replace />;
  }
  return children;
}

function RedirectStaffFromApplicant({ children }) {
  const role = getAuthRole();
  if (role === 'advisor') return <Navigate to={ADVISOR_HOME_PATH} replace />;
  if (role === 'admin' || role === 'super_admin') return <Navigate to={ORG_SLATE_PATH} replace />;
  return children;
}

function LegacyAdminToAdvisor() {
  return <Navigate to={localizedPath('/advisor')} replace />;
}

function LegacyAdminCaseToAdvisor() {
  const { userId, sessionId } = useParams();
  return <Navigate to={localizedPath(`/advisor/cases/${userId}/${sessionId}`)} replace />;
}

/** Old /es/login bookmarks → /login, keeping ?ref= and other query params. */
function RedirectEsLogin() {
  const { search } = useLocation();
  return <Navigate to={`/login${search || ''}`} replace />;
}

/** Site root is the sign-in page, not a 404. Keep query params (e.g. ?ref=signin). */
function RedirectRootToLogin() {
  const { search } = useLocation();
  return <Navigate to={`/login${search || ''}`} replace />;
}

function ProfileSetupPage() {
  const navigate = useNavigate();
  return (
    <ProfileSetup
      mode="edit"
      onComplete={() => navigate('/account', { replace: true })}
    />
  );
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PostHogPageView />
      <ScrollToTop />
      <RefGuard>
        <Routes>
          <Route path="/" element={<RedirectRootToLogin />} />
          <Route path="/es/regularizacion" element={<Landing />} />
          <Route path="/r/:token" element={<ReferralRedirect />} />
          <Route path="/signup" element={<CustomerSignup />} />
          <Route path="/participant" element={<RedirectStaffFromApplicant><FieldworkParticipant /></RedirectStaffFromApplicant>} />
          <Route path="/signin" element={<Signup />} />
          <Route path="/es/regularizacion/registro" element={<Signup />} />
          <Route path="/qualify" element={<NotFound404 />} />
          <Route path="/qualify/qualified" element={<NotFound404 />} />
          <Route path="/es/regularizacion/qualify" element={<Qualify />} />
          <Route path="/es/regularizacion/qualify/qualified" element={<QualifyQualified />} />
          <Route path="/login" element={<Login />} />
          <Route path="/es/login" element={<RedirectEsLogin />} />
          <Route path="/intake" element={<Navigate to={localizedPath('/intake')} replace />} />
          <Route path="/organizer" element={<Navigate to={localizedPath('/organizer')} replace />} />
          <Route path="/advisor" element={<Navigate to={localizedPath('/advisor')} replace />} />
          <Route path="/register" element={<ProtectedRoute><Registration /></ProtectedRoute>} />
          <Route path="/signup/sso-details" element={<ProtectedRoute><SsoSignupDetails /></ProtectedRoute>} />
          <Route path="/signup/sso-contact" element={<ProtectedRoute><SsoSignupContact /></ProtectedRoute>} />
          <Route path="/success" element={<NotFound404 />} />
          <Route path="/es/regularizacion/success" element={<ProtectedRoute><Success /></ProtectedRoute>} />
          <Route path="/welcome" element={<ExistingUserWelcome />} />
          <Route element={<ApplicantPortalRoot />}>
            <Route path="/es/intake" element={<LoginRedirectRoute><ApplicantPortalContent /></LoginRedirectRoute>} />
            <Route path="/es/organizer" element={<LoginRedirectRoute><ApplicantPortalContent /></LoginRedirectRoute>} />
          </Route>
          <Route path="/welcome-existing" element={<Navigate to="/welcome" replace />} />
          <Route path="/getting-started" element={<ProtectedRoute><GettingStarted /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/refer" element={<ProtectedRoute><ReferPage /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><ReferralListPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfileSetupPage /></ProtectedRoute>} />
          <Route element={<AdvisorOnlyRoute><AdvisorPortalLayout /></AdvisorOnlyRoute>}>
            <Route path="/es/advisor" element={<AdvisorCustomerSearch />} />
            <Route path="/customer/new" element={<AdvisorCustomerNew />} />
            <Route path="/customer/:customerId" element={<AdvisorCustomerProfile />} />
            <Route path="/case/:caseId" element={<AdvisorCaseDetail />} />
            <Route path="/family/:familyGroupId" element={<AdvisorFamilyView />} />
          </Route>
          <Route path="/advisor/cases/:userId/:sessionId" element={<AdvisorOnlyRoute><AdminCaseDetail /></AdvisorOnlyRoute>} />
          <Route path="/admin" element={<AdvisorOnlyRoute><LegacyAdminToAdvisor /></AdvisorOnlyRoute>} />
          <Route path="/admin/cases/:userId/:sessionId" element={<AdvisorOnlyRoute><LegacyAdminCaseToAdvisor /></AdvisorOnlyRoute>} />
          <Route path="/agent" element={<AdvisorOnlyRoute><AgentConsole /></AdvisorOnlyRoute>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/dpa" element={<Dpa />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/org-signup" element={<OrgSignup />} />
          <Route path="/org/projects/new" element={<BlockAdvisorFromOrg><OrgCreateProject /></BlockAdvisorFromOrg>} />
          <Route path="/org/projects/moviemagic" element={<Navigate to="/org/productions/new" replace />} />
          <Route path="/org/productions/new" element={<BlockAdvisorFromOrg><OrgMovieMagicCrew /></BlockAdvisorFromOrg>} />
          <Route path="/org/productions/:productionId" element={<BlockAdvisorFromOrg><OrgProductionDetails /></BlockAdvisorFromOrg>} />
          <Route path="/org/productions/:productionId/participants/:participantId" element={<BlockAdvisorFromOrg><OrgParticipantDetail /></BlockAdvisorFromOrg>} />
          <Route path="/org/productions/:productionId/requests/:taskSlug" element={<BlockAdvisorFromOrg><OrgRequestDetail /></BlockAdvisorFromOrg>} />
          <Route path="/org/slate" element={<BlockAdvisorFromOrg><OrgAdvisorDashboard /></BlockAdvisorFromOrg>} />
          <Route path="/org/admin" element={<BlockAdvisorFromOrg><OrgAdminTools /></BlockAdvisorFromOrg>} />
          <Route path="/org/dashboard" element={<Navigate to="/org/slate" replace />} />
          <Route path="/org-reset-password" element={<OrgResetPassword />} />
          <Route path="/login-error" element={<LoginError />} />
          <Route path="/:country/:procedurePath" element={<ProcedureShell />}>
            <Route index element={<ProcedureLanding />} />
            <Route path="qualify" element={<Qualify />} />
            <Route path="qualify/qualified" element={<QualifyQualified />} />
            <Route path="registro" element={<Signup />} />
            <Route path="success" element={<ProtectedRoute><Success /></ProtectedRoute>} />
            {/* FormProvider + LanguageProvider from ApplicantPortalRoot (sign & submit). */}
            <Route element={<ApplicantPortalRoot />}>
              <Route path="intake" element={<LoginRedirectRoute><ProcedureIntakePage /></LoginRedirectRoute>} />
            </Route>
          </Route>
          <Route path="/:partnerToken" element={<PartnerLinkRedirect />} />
          <Route path="*" element={<NotFound404 />} />
        </Routes>
      </RefGuard>
    </BrowserRouter>
  );
}
